<template>
  <v-menu
    :openOnClick="false"
    ref="cmdMenu"
    transition="false"
    class="pl-2"
    nudge-bottom="-8"
    allow-overflow
    offset-y
    top
    max-height="400"
    style="height: auto"
    max-width="600"
    v-model="showCmds"
  >
    <template v-slot:activator="{ on }">
      <v-text-field
        @keydown.ctrl.space="cmdsVisible = true"
        @keydown.tab="selectCmd"
        @keydown.enter="validSaisie"
        :disabled="disabled"
        v-on="on"
        :hide-details="hint.length == 0 && cmdError.length == 0"
        :hint="hint"
        :error-messages="cmdError"
        v-model="command"
      >
        <template #append>
          <span v-if="unit?.length" class="ml-2">{{ unit }}</span>
        </template>
      </v-text-field>
    </template>
    <v-list>
      <v-list-tile
        :title="cmd.comment"
        v-for="(cmd, idx) in searchCmds"
        :class="{ 'v-list__tile--highlighted': getSelectedCmdIdx() == idx }"
        :value="getSelectedCmdIdx() == idx"
        :key="cmd.label"
        @click="selectCommand(cmd)"
      >
        <v-list-tile-content>
          <v-list-tile-title>
            <strong>
              <text-highlight :queries="command">{{ cmd.label }}</text-highlight>
            </strong>
          </v-list-tile-title>
          <v-list-tile-sub-title v-if="cmd.comment">
            <i class="caption">{{ cmd.comment }}</i>
          </v-list-tile-sub-title>
        </v-list-tile-content>
      </v-list-tile>
    </v-list>
  </v-menu>
</template>

<script lang="ts">
import { DataPoolGlobalKeys } from '@/enum/DataPool'
import { GliderCmd } from '@/models/datapool/GliderCmd'
import Vue from 'vue'
import Component from 'vue-class-component'
import { Watch } from 'vue-property-decorator'

@Component({
  name: 'GliderCmdInput',
  props: {
    value: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      default: '',
    },
    //modèle du glider (GLI_FABRICANT, ex. 'SXP'/'DXP') — si fourni, filtre la complétion par type
    gliderFabricant: {
      type: String,
      default: null,
    },
  },
})
export default class GliderCmdInput extends Vue {
  cmdsVisible = false
  value!: string
  gliderFabricant!: string | null
  cmdSended = false
  cmdError = ''

  created() {
    this.$store.myActions.DATAPOOL_ENSURE({ fullKey: DataPoolGlobalKeys['global.config.cmdCompletion'] })
  }

  get command() {
    return this.value
  }

  set command(newValue: string) {
    this.$emit('input', newValue)
  }

  @Watch('command')
  onCommandChanged(newValue: String, oldValue: String) {
    if (newValue != oldValue + ';') this.cmdError = ''
    if (newValue != '') this.cmdsVisible = true
    else if (this.cmdSended) this.cmdsVisible = false
  }

  get originalCmdListStr() {
    //@ts-ignore
    const list = (
      this.$store.myGetters.dataPoolItemsValueByKey(DataPoolGlobalKeys['global.config.cmdCompletion']) || ''
    )
      .split('\n')
      //@ts-ignore
      .filter(t => t && t.length > 0)
      .sort()
    return list.join('\n')
  }

  get cmds() {
    //si le type du glider est connu : lignes génériques + lignes typées, la typée surcharge la générique à KEY égale
    const allCmds = GliderCmd.filterByGliderType(
      GliderCmd.createGliderCmdArray(this.originalCmdListStr),
      this.gliderFabricant,
    )
    return [
      ...allCmds
        .filter(c => c.COMMAND_ACCESS)
        .map(c => {
          return {
            label: `$${c.KEY}`,
            comment: c.COMMENTS,
            range: c.RANGE,
            excluded: c.EXCLUDED,
            unit: c.UNIT,
            hint: [
              `${c.RANGE && c.RANGE.length && c.RANGE != '[-]' ? `${this.$t('GliderInput.Range')}: ${c.RANGE}` : ''}`,
              `${c.EXCLUDED && c.EXCLUDED.length ? `${this.$t('GliderInput.Excluded')}: ${c.EXCLUDED}` : ''}`,
              `${c.DEFAULT && c.DEFAULT.length ? `${this.$t('GliderInput.default')}: ${c.DEFAULT}` : ''}`,
            ]
              .filter(t => t && t.length)
              .join(', '),
          }
        }),
      ...allCmds
        .filter(c => c.READABLE)
        .map(c => {
          return {
            label: `?${c.KEY}`,
            comment: c.COMMENTS.startsWith('Sets ')
              ? 'Get ' + c.COMMENTS.substring(5).trim()
              : c.COMMENTS.startsWith('Set ')
              ? 'Get ' + c.COMMENTS.substring(4).trim()
              : 'Get ' + c.COMMENTS,
            range: c.RANGE,
            excluded: c.EXCLUDED,
            unit: c.UNIT,
            hint: [
              `${c.RANGE && c.RANGE.length && c.RANGE != '[-]' ? `${this.$t('GliderInput.Range')}: ${c.RANGE}` : ''}`,
              `${c.EXCLUDED && c.EXCLUDED.length ? `${this.$t('GliderInput.Excluded')}: ${c.EXCLUDED}` : ''}`,
              `${c.DEFAULT && c.DEFAULT.length ? `${this.$t('GliderInput.default')}: ${c.DEFAULT}` : ''}`,
            ]
              .filter(t => t && t.length)
              .join(', '),
          }
        }),
    ]
  }

  get searchCmds() {
    return this.cmds.filter(c => this.command == '' || c.label.indexOf(this.command) >= 0)
  }

  get searchCmdsLength() {
    return this.searchCmds.length
  }
  @Watch('searchCmdsLength')
  onSearchCmdsLengthChanged(newValue: Number) {
    this.cmdsVisible = !this.cmdsVisible
    this.$nextTick(() => (this.cmdsVisible = !this.cmdsVisible))
  }

  get showCmds() {
    return this.cmdsVisible && this.searchCmdsLength > 0 && this.command.length > 0
  }

  set showCmds(newValue) {
    this.cmdsVisible = newValue
  }

  get currentSetCmd() {
    const idxEqual = this.command.indexOf('=')
    const idxComma = this.command.indexOf(';')
    const idx = idxEqual > -1 ? idxEqual : idxComma > -1 ? idxComma : this.command.length
    if (idx > -1 && this.command.indexOf('$') == 0) {
      const cmd = this.cmds.filter(c => c.label == this.command.substring(0, idx))
      return cmd?.at(0) || null
    }
    return null
  }

  get currentAsktCmd() {
    if (this.command.indexOf('?') == 0) {
      const cmd = this.cmds.filter(
        c => c.label == (this.command.endsWith(';') ? this.command.slice(0, -1) : this.command),
      )
      return cmd?.at(0) || null
    }
    return null
  }

  get hint() {
    return this.currentSetCmd?.hint || ''
  }

  get unit() {
    return this.currentSetCmd?.unit || ''
  }

  @Watch('showCmds')
  onShowCmdsChanged(newValue: Boolean) {
    //@ts-ignore
    this.$refs.cmdMenu.listIndex = 0
  }

  selectCommand(cmd: { label: string }) {
    this.command = cmd.label
  }

  getSelectedCmdIdx() {
    //@ts-ignore
    let idx = this.$refs.cmdMenu ? this.$refs.cmdMenu.listIndex : 0
    return Math.max(idx, 0)
  }

  selectCmd(e: Event) {
    if (this.showCmds) {
      e.preventDefault()
      e.stopPropagation()
      this.doSelectCmd()
    }
  }

  doSelectCmd() {
    const index = this.getSelectedCmdIdx()
    if (index > -1) this.selectCommand(this.searchCmds[index])
  }

  async validSaisie() {
    this.cmdSended = true
    if (this.showCmds) {
      setTimeout(() => {
        this.cmdsVisible = false
      }, 500)
    } else {
      this.$emit('validSaisie')
      this.cmdsVisible = false
    }
    setTimeout(() => (this.cmdSended = false), 4000)
  }

  //la methode pour valider la saisie est appelée à l'exterieur du composant à cause du bouton valider externe
  async checkSaisie() {
    //cas pas une commande de set on autorise tjs d'envoyer
    if (!(this.command.indexOf('$') == 0) && !(this.command.indexOf('?') == 0)) {
      this.cmdError = this.$t('GliderInput.ErrorStartCommand') as string
      return false
    }
    /*if (this.command.indexOf('$') == 0 && this.command.indexOf('=') == -1) {
      this.cmdError = this.$t('GliderInput.ErrorValueCommand') as string
      return false
    }*/
    if (!this.currentSetCmd && !this.currentAsktCmd) {
      this.cmdError = this.$t('GliderInput.ErrorUnknownCommand') as string
      return true
    }
    //cas commande de set on verifie la valeur
    if (this.currentSetCmd) {
      if (this.currentSetCmd.range?.length && this.currentSetCmd.range != 'N/A' && this.currentSetCmd.range != '[-]') {
        //Extract the value of the command after the '=' sign and strip an eventual ';' at the end and be carrefful to not replace a potential ';' in the value itself
        const value = this.command.substring(this.command.indexOf('=') + 1).replace(/;$/, '')
        //Analyse les ranges potentiels les blocs de range possible sont séparés par des ',' et les ranges sont de deux types poissible :
        // - range de type [min:max]  exemple [0:100] ou [-50:50] dans le cas ou une des valeur n'est pas un nombre alors on considère que il n'y a pas de borne sur ce coté du range les bornes peuvent etre inclusif ou exclusif en fonction de [ ou ] selon la position du crochet
        // - range de type liste d'option exemple {2:655:6} ou chaque valeur possible est séparée par ':' et est un nombre on n'oublie pas d'enlever les { et } avant le split des :
        const rangeBlocks = this.currentSetCmd.range.split(',')
        let valid = false
        for (const block of rangeBlocks) {
          if ((block.startsWith('[') || block.startsWith(']')) && (block.endsWith(']') || block.endsWith('['))) {
            const rangeValues = block.substring(1, block.length - 1).split(':')
            if (rangeValues.length == 2) {
              const min = rangeValues[0].trim() == '' ? Number.NEGATIVE_INFINITY : Number(rangeValues[0].trim())
              const max = rangeValues[1].trim() == '' ? Number.POSITIVE_INFINITY : Number(rangeValues[1].trim())
              if (
                (isNaN(min) ||
                  (block.startsWith('[') && Number(value) >= min) ||
                  (block.startsWith(']') && Number(value) > min)) &&
                (isNaN(max) ||
                  (block.endsWith(']') && Number(value) <= max) ||
                  (block.endsWith('[') && Number(value) < max))
              )
                valid = true
            }
          } else if (block.startsWith('{') && block.endsWith('}')) {
            const possibleValues = block
              .substring(1, block.length - 1)
              .split(':')
              .map(v => v.trim())
            if (possibleValues.includes(value)) valid = true
          }
        }

        //on verifie aussi que la valeur n'est pas dans les valeurs exclues
        //le format des valeurs exclues est {2:655:6} ou chaque valeur possible est séparée par ':' et est un nombre on n'oublie pas d'enlever les { et } avant le split des :
        if (
          this.currentSetCmd.excluded?.length &&
          this.currentSetCmd.excluded.startsWith('{') &&
          this.currentSetCmd.excluded.endsWith('}')
        ) {
          const excludedValues = this.currentSetCmd.excluded
            .substring(1, this.currentSetCmd.excluded.length - 1)
            .split(':')
            .map(v => v.trim())
          if (excludedValues.includes(value)) valid = false
        }

        if (!valid) {
          this.cmdError = this.$t('GliderInput.ErrorRangeValue') as string

          return await this.$confirm(this.$t('GliderInput.ErrorRangeValue') as string, {
            title: '',
            buttonTrueText: 'Ok',
            buttonFalseText: 'No',
          })
        }
      }
    }
    //dans les autres cas on valide tjs
    this.cmdError = ''
    return true
  }
}
</script>
